/*
 * CPCV — Cost Per Completed View, as a governed presentation.
 *
 * The METRIC itself is not defined here. `costPerCompletedView` already exists
 * in the canonical registry (metricBasis.js, byte-pinned across Cortex,
 * Quantum and the backend twin): client-billed spend over completed views,
 * classified SEMANTIC because sources do not agree on what finishing a video
 * means. This module is the thin layer above it that answers the two questions
 * a KPI strip actually asks:
 *
 *   which platforms are READ on cost per completed view at all?
 *   and what does the tile say when the completion count is not there?
 *
 * WHY THIS REPLACES CONVERSIONS RATHER THAN JOINING THE STRIP
 *
 * On video-first inventory the Conversions tile was never a reading of the
 * platform. The focused Platform Analytics model deliberately sets conversions
 * to null when one platform is soloed — the canonical payload reports
 * conversions for the WINDOW, not per channel, and re-attributing a
 * whole-window figure to one platform would be a claim nobody made. So the
 * tile occupied a KPI slot to print `—` and a disclaimer about its own scope.
 * CPCV puts a real, platform-scoped, governed efficiency number in that slot
 * for the inventory that is bought on completed viewing, and leaves Conversions
 * exactly where it is meaningful.
 *
 * WHICH PLATFORMS
 *
 * `VIDEO_FIRST_PLATFORM_KEYS` — the existing governed ruling for "sold and
 * judged on completed viewing rather than clicks" (CTV, connected TV,
 * YouTube). Deliberately NOT the broader video-rate-capable set: a Meta or
 * TikTok video campaign reports video counts but is bought and optimized on
 * clicks and conversions, and swapping its Conversions tile would regress a
 * channel where conversions are the point. One ruling, already written down,
 * consulted here rather than restated as a platform conditional in a component.
 *
 * SPEND BASIS
 *
 * There is no basis arithmetic in this module and there must never be. The
 * numerator is read by the canonical engine through the same client-billed
 * alias ladder (`billedSpend` → `clientFacingSpend` → `spend`) that produces
 * the Spend tile's own number, from the same already-basis-resolved
 * observation. A surface showing gross Spend therefore cannot show a net CPCV
 * beside it: they are not two derivations of one basis, they are one number
 * divided. Nothing here multiplies by a margin, and nothing here may.
 */

import { aggregateMetricBasis, BASIS_EXCLUSION_REASONS } from "./metricBasis";
import { VIDEO_FIRST_PLATFORM_KEYS } from "./platformEfficiencyMetric";

export const COST_PER_COMPLETED_VIEW_KEY = "costPerCompletedView";
export const COST_PER_COMPLETED_VIEW_LABEL = "CPCV";
export const COST_PER_COMPLETED_VIEW_LONG_LABEL = "Cost Per Completed View";

/*
 * CPV and CPCV are different metrics and the copy must keep them apart. CPV
 * divides spend by video VIEWS — a view is counted early, on a threshold the
 * platform sets. CPCV divides the same spend by views that reached the end.
 */
export const COST_PER_COMPLETED_VIEW_DEFINITION =
  "Cost per completed video view — client-billed spend divided by the completed views the platform reported. Not CPV: a view is counted at the platform's own early threshold, a completed view reached the end.";

/*
 * The unavailable copy names the missing FACT, not the missing number. A
 * viewer who reads "—" needs to know the completion count never arrived, so
 * they look upstream at the source rather than at the campaign.
 */
export const COMPLETED_VIEWS_UNREPORTED_NOTE = "Completed views not reported";

const normalizePlatformKey = (value) =>
  String(value == null ? "" : value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

/**
 * Is this platform READ on cost per completed view?
 *
 * A property of the inventory, not of the payload. A CTV row that reported no
 * completions is still a CTV row: it shows the CPCV tile in its unavailable
 * state rather than falling back to a Conversions tile, because the reason
 * there is no number is a gap in the source, and swapping the metric would
 * hide that gap behind a different label.
 */
export function platformReadsCostPerCompletedView(platformKey) {
  return VIDEO_FIRST_PLATFORM_KEYS.has(normalizePlatformKey(platformKey));
}

/**
 * Which metric owns the outcome slot in a KPI strip scoped to `platformKey`.
 *
 * The single platform-aware ruling. Every surface calls this instead of
 * testing for "ctv" or "youtube" itself, so the four Platform Analytics states
 * across two repositories cannot drift into four different opinions.
 *
 * A null / absent / unrecognised key is the all-platforms scope and keeps
 * Conversions: a blended CPCV across channels that do not share a completion
 * definition is exactly the manufactured number the SEMANTIC classification
 * exists to refuse.
 */
export function resolveOutcomeMetricKey(platformKey) {
  return platformReadsCostPerCompletedView(platformKey)
    ? COST_PER_COMPLETED_VIEW_KEY
    : "conversions";
}

/**
 * CPCV for one scope, through the canonical engine.
 *
 * `observations` are the same platform rows every other governed rate is
 * folded over. The engine decides eligibility and refuses an unprovable
 * blend; this only translates its verdict into what a tile needs.
 *
 * The returned `reason` distinguishes the two states that look identical on
 * screen but mean different things upstream: MISSING_DENOMINATOR is "this
 * source reports no completion count", UNREPORTED is "this scope reported no
 * spend". Both render `—`; neither renders 0.
 */
export function resolveCostPerCompletedView(observations = []) {
  const rows = Array.isArray(observations) ? observations : [];
  const scoped = rows.filter((row) =>
    platformReadsCostPerCompletedView(row?.platformKey ?? row?.key)
  );

  const basis = aggregateMetricBasis(COST_PER_COMPLETED_VIEW_KEY, scoped);
  const value =
    basis && basis.comparable && Number.isFinite(basis.value) && basis.value >= 0
      ? basis.value
      : null;

  return {
    metricKey: COST_PER_COMPLETED_VIEW_KEY,
    label: COST_PER_COMPLETED_VIEW_LABEL,
    longLabel: COST_PER_COMPLETED_VIEW_LONG_LABEL,
    definition: COST_PER_COMPLETED_VIEW_DEFINITION,
    unit: "currency",
    applicable: scoped.length > 0,
    value,
    spend: basis?.numerator ?? null,
    completedViews: basis?.denominator ?? null,
    comparable: basis ? basis.comparable : false,
    eligiblePlatformKeys: basis?.eligiblePlatformKeys || [],
    excludedPlatformKeys: basis?.excludedPlatformKeys || [],
    reason:
      value != null
        ? null
        : basis && basis.comparable === false
        ? BASIS_EXCLUSION_REASONS.DEFINITION_INCOMPATIBLE
        : (basis?.exclusions || [])[0]?.reason ||
          BASIS_EXCLUSION_REASONS.MISSING_DENOMINATOR,
    unavailableNote: value != null ? null : COMPLETED_VIEWS_UNREPORTED_NOTE,
  };
}

/**
 * CPCV as money, to the cent.
 *
 * Stated at the same resolution as every other cost on the page, which is the
 * point: a strip whose CPM and CPC read in cents and whose CPCV read in
 * ten-thousandths invites the reading that CPCV is a different KIND of number.
 * The rounding is real and worth knowing about — NSC's CTV is $0.0472 and
 * shows as $0.05, about 6% high — so a figure that needs the underlying
 * precision should be read off the counts, which the strip shows beside it.
 *
 * One case gets special handling. A completed view can cost less than half a
 * cent, and rounding that to `$0.00` would say the delivery was free. Below a
 * cent the tile reads `<$0.01` — small is not the same as none, the same
 * distinction `formatRatePercent` makes with `<0.01%`. A value that is
 * genuinely zero still prints `$0.00`, because that is a claim the counts
 * actually made.
 *
 * `—` is the only thing an ABSENT value may render as.
 */
export function formatCostPerCompletedView(value) {
  if (!Number.isFinite(value) || value < 0) return "—";
  if (value > 0 && value < 0.005) return "<$0.01";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * The same value at the resolution the buy is actually negotiated at.
 *
 * Not for display — for the sentence behind it. Every money figure the page
 * SHOWS is stated to the cent: the KPI tile, the dedicated platform card and
 * the Creative Performance table all round the same way, so nothing on screen
 * invites the reading that CPCV is a different kind of number than the CPM
 * beside it.
 *
 * That rounding costs real information. A completed view is worth cents, so
 * NSC's $0.0472 and $0.0471 creatives both read $0.05 and the cent figure
 * cannot separate them. This function is where the precision survives: the
 * help text carries the exact value, so a reader who needs to tell two buys
 * apart has it without doing arithmetic off the counts.
 *
 * Keep it out of tiles and tables. The moment a fourth decimal appears next to
 * a two-decimal CPM, the page is back to claiming two resolutions of one
 * metric — which is what this split exists to avoid.
 */
export function formatCostPerCompletedViewPrecise(value) {
  if (!Number.isFinite(value) || value < 0) return "—";
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;
}

/**
 * The help sentence for a resolved CPCV, with its exact value when there is
 * one. One implementation, so the KPI tile and the dedicated platform card
 * explain the metric identically.
 */
export function describeCostPerCompletedView(value) {
  if (!Number.isFinite(value) || value < 0) return COST_PER_COMPLETED_VIEW_DEFINITION;
  return `${COST_PER_COMPLETED_VIEW_DEFINITION} Exact for this window: ${formatCostPerCompletedViewPrecise(
    value
  )} per completed view.`;
}
